import type React from "react";
import { useCallback, useMemo } from "react";
import { SearchInput } from "@similarweb/ui-components";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import {
    SearchContainer,
    ButtonsContainer,
    DownloadExcelContainer,
} from "app/pages/industry-analysis/category-share/CategoryShareTable/CategoryShareTableTop/CategoryShareTableTopStyles";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { createTableFilter, getColumnsPickerLiteProps } from "./CategoryShareTableTopUtils";
import type { ICategoryShareTableTopProps } from "./CategoryShareTableTopTypes";
import { CategoryShareTableTopContainer } from "app/pages/industry-analysis/category-share/CategoryShareTable/CategoryShareTableTop/CategoryShareTableTopStyles";

const TABLE_FILTER_KEY = "Domain";
const TABLE_EXCEL_API = "/api/Market/Trends/Excel";

const resolvedExcelLink = (tableApiQueryParams: any, TABLE_EXCEL_API, services, sort) => {
    const urlParams: string = {
        ...tableApiQueryParams,
        sort: sort.field,
        asc: sort.sortDirection === "asc",
    };
    const excelApiParams = services.fetchService.requestParams(urlParams);
    const excelApiURL = `${TABLE_EXCEL_API}?${excelApiParams}`;
    return excelApiURL;
};

export const CategoryShareTableTop: React.FunctionComponent<ICategoryShareTableTopProps> = (props) => {
    const { onFilterChange, tableColumns, onClickToggleColumns, services, tableApiQueryParams, sort } = props;

    const onSearch = useCallback(
        (search: string) => {
            const filterValue = search ? { filter: createTableFilter(TABLE_FILTER_KEY, search) } : { filter: "" };

            onFilterChange(filterValue, false);
        },
        [onFilterChange],
    );

    const columnPickerProps = useMemo(() => {
        return getColumnsPickerLiteProps(tableColumns, onClickToggleColumns);
    }, [tableColumns, onClickToggleColumns]);

    const excelApiURL = resolvedExcelLink(tableApiQueryParams, TABLE_EXCEL_API, services, sort);
    const isExcelEnabled = services.swSettings.current.resources.IsExcelAllowed;

    return (
        <CategoryShareTableTopContainer>
            <SearchContainer>
                <SearchInput
                    defaultValue={""}
                    debounce={250}
                    onChange={onSearch}
                    placeholder={services.translate("table.search.placeholder.default")}
                />
            </SearchContainer>
            <ButtonsContainer>
                <DownloadExcelContainer>
                    <DownloadButtonMenu
                        Excel={true}
                        downloadUrl={excelApiURL}
                        excelLocked={!isExcelEnabled}
                    />
                </DownloadExcelContainer>
                <div>
                    <ColumnsPickerLite
                        {...columnPickerProps}
                        withTooltip={true}
                        translations={{
                            tooltip: services.translate("workspace.sales.column.picker"),
                            getSelectedAmountText: ({ selectedValue, totalValue }) => services.translate("common.columnsPicker.selectedAmount", {
                                selected: selectedValue,
                                total: totalValue,
                            }),
                            mandatoryField: services.translate("common.columnsPicker.mandatoryField"),
                            apply: services.translate("common.apply"),
                        }}
                    />
                </div>
            </ButtonsContainer>
        </CategoryShareTableTopContainer>
    );
};
